import { styled } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import MuiDrawer, { drawerClasses } from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuContent from './MenuContent';
import { useNavigate } from 'react-router-dom';
import { useSiteSession } from '../../context/SiteSessionContext';
import { useAuth } from '../../context/AuthContext';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

const drawerWidth = 260;
const collapsedDrawerWidth = 82;

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})(({ collapsed }) => ({
  width: collapsed ? collapsedDrawerWidth : drawerWidth,
  flexShrink: 0,
  boxSizing: 'border-box',
  [`& .${drawerClasses.paper}`]: {
    width: collapsed ? collapsedDrawerWidth : drawerWidth,
    boxSizing: 'border-box',
    transition: 'width 180ms ease',
    overflowX: 'hidden',
  },
}));

export default function SideMenu({ collapsed = false, onToggleCollapsed }) {
  const navigate = useNavigate();
  const { isAdmin, site, disconnect } = useSiteSession();
  const { account, logout } = useAuth();
  const isLogisticsCollaborator = account?.authType === 'logistics_user';
  const collaboratorLabel = [account?.firstName, account?.lastName].filter(Boolean).join(' ');

  const handleDisconnect = () => {
    disconnect();
    logout();
    navigate('/connexion');
  };

  return (
    <Drawer
      variant="permanent"
      collapsed={collapsed}
      sx={{
        display: { xs: 'none', md: 'block' },
        [`& .${drawerClasses.paper}`]: {
          backgroundColor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      }}
    >
      <Box sx={{ p: 1, pt: 2 }}>
        <Box
          sx={{
            display: 'flex',
            p: collapsed ? 1 : 1.5,
            alignItems: 'center',
            gap: 1.5,
            justifyContent: collapsed ? 'center' : 'flex-start',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.default',
          }}
        >
          <Box
            component="img"
            src="/optibox-logo.svg"
            alt="Optibox"
            sx={{
              width: collapsed ? 44 : 50,
              height: collapsed ? 44 : 50,
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
          {!collapsed && (
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
              Optibox
            </Typography>
          </Box>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={onToggleCollapsed}
          sx={{
            mt: 1,
            ml: collapsed ? 1.4 : 'auto',
            display: 'flex',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {collapsed ? <ChevronRightRoundedIcon fontSize="small" /> : <ChevronLeftRoundedIcon fontSize="small" />}
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ overflow: 'auto', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <MenuContent collapsed={collapsed} />
      </Box>
      <Stack
        direction="row"
        sx={{
          p: 1.5,
          gap: 1,
          alignItems: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Avatar sizes="small" sx={{ width: 36, height: 36 }}>
          {isLogisticsCollaborator
            ? `${account?.firstName?.[0] || ''}${account?.lastName?.[0] || ''}`.toUpperCase()
            : isAdmin ? 'A' : site?.type === 'store' ? 'M' : 'W'}
        </Avatar>
        {!collapsed && (
        <Box sx={{ mr: 'auto' }}>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: '16px' }}>
            {isLogisticsCollaborator ? collaboratorLabel : isAdmin ? 'Administrateur' : site?.name}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {isLogisticsCollaborator ? 'Logistique' : isAdmin ? 'Vue globale' : site?.type === 'store' ? 'Magasin' : site?.type === 'sav' ? 'Logistique' : 'Atelier'}
          </Typography>
        </Box>
        )}
        {!collapsed && <Button size="small" onClick={handleDisconnect}>
          Sortir
        </Button>}
      </Stack>
    </Drawer>
  );
}
